/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Table from 'components/Table';
import PropTypes from 'prop-types';
import {
  Collapse,
  Row,
  Col,
  Typography,
  Button,
  message,
  Tooltip,
  Checkbox,
  Dropdown,
  Menu,
  Spin,
  Popconfirm,
} from 'antd';
import currencyFormater from 'utils/toCurrency';
import { useSelector, useDispatch } from 'react-redux';
import round from 'utils/round';
import {
  CommentOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
  PieChartOutlined,
  PlusOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons';
import {
  ADD_PADRON,
  REMOVE_PADRON,
  SET_CARGOS_PADRON,
  SET_PADRON_PAGO_CONVENIO,
  SET_PIN_PADRON_AGREGAR_CARGOS,
  SET_PIN_PADRON_ALERTAS,
  SET_PIN_PADRON_COMENTARIO,
  SET_PIN_PADRON_INFO,
  SET_SELECTEDROWKEYS,
} from 'store/reducers/caja';
import { titleize, toMoment } from 'utils/normalizers';
import padronToString from 'utils/padronesToString';
import { postConsultarCargosPorPadron, postConsultarCargosPublicoGeneral } from 'api/recaudacion';
import { getConvenio } from 'api/recaudacion/convenios';
import { postObtenerReporte, REPORTE_DOCUMENTO_CONVENIO } from 'api/reporteador';
import { downloadBase64 } from 'components/Previewer';
import { padronesEnum } from 'views/Tramites/GPM/enums';

const { Text } = Typography;

const isDev = process.env.NODE_ENV === 'development';
const roundToTwo = (num) => +(`${Math.round(`${num}e+2`)}e-2`);
const filterPorPeriodo = (d) => d.aplica_por_periodo && d.aplicado;
const filterNoPorPeriodo = (d) => !d.aplica_por_periodo && d.aplicado;

const tooltipoInfo = (record) => {
  if (record?.observaciones) {
    return (
      <Tooltip title={record.observaciones}>
        <InfoCircleOutlined />
      </Tooltip>
    );
  }
  return (<span />);
};

const descuentosMapper = (d) => (
  <li key={d.id}>
    {isDev && `(${d.id}) `}
    {`${d.comentarios || ''}: ${currencyFormater(d.importe_total || 0)}`}
  </li>
);

/**
 * Processes discounts by applying a filter function and mapping the results to a formatted list.
 * @param {Array} items - The list of items containing discounts.
 * @param {Function} filterFn - The filter function to apply to the discounts.
 * @returns {Array} - A list of formatted discounts that match the filter criteria.
 */
const generateLabels = (items, filterFn = (d) => d.aplicado) => (items[0]?.descuentos
  ? items.map((item) => item.descuentos).flat().filter(filterFn).map(descuentosMapper)
  : items.filter(filterFn).map(descuentosMapper));

const some = (arr) => arr.some((e) => e.length);

// region Descuentos Render
export const descuentosRender = (txt, record) => {
  const {
    descuentos_aplicables,
    descuentos_especiales,
    recargos,
    recargosMulta,
    gastos,
    gastosMulta,
    actualizaciones,
  } = record;

  // Generate labels for all discount categories
  const aplicables = generateLabels(descuentos_aplicables, filterNoPorPeriodo);
  const aplicablesPorPeriodo = generateLabels(descuentos_aplicables, filterPorPeriodo);
  const especiales = generateLabels(descuentos_especiales, filterNoPorPeriodo);
  const especialesPorPeriodo = generateLabels(descuentos_especiales, filterPorPeriodo);
  const sobreRecargos = generateLabels(recargos, filterNoPorPeriodo);
  const sobreRecargosPorPeriodo = generateLabels(recargos, filterPorPeriodo);
  const sobreRecargosMulta = generateLabels(recargosMulta, filterNoPorPeriodo);
  const sobreRecargosMultaPorPeriodo = generateLabels(recargosMulta, filterPorPeriodo);
  const sobreGastos = generateLabels(gastos, filterNoPorPeriodo);
  const sobreGastosPorPeriodo = generateLabels(gastos, filterPorPeriodo);
  const sobreGastosMulta = generateLabels(gastosMulta, filterNoPorPeriodo);
  const sobreGastosMultaPorPeriodo = generateLabels(gastosMulta, filterPorPeriodo);
  const sobreActualizacion = generateLabels(actualizaciones, filterNoPorPeriodo);
  const sobreActualizacionPorPeriodo = generateLabels(actualizaciones, filterPorPeriodo);

  // Check if there are any discounts
  const hasSomeDiscount = some([
    aplicables,
    aplicablesPorPeriodo,
    especiales,
    especialesPorPeriodo,
    sobreRecargos,
    sobreRecargosPorPeriodo,
    sobreRecargosMulta,
    sobreRecargosMultaPorPeriodo,
    sobreGastos,
    sobreGastosPorPeriodo,
    sobreGastosMulta,
    sobreGastosMultaPorPeriodo,
    sobreActualizacion,
    sobreActualizacionPorPeriodo,
  ]);

  // Generate tooltip content
  const title = hasSomeDiscount ? (
    <ul>
      {some([aplicables, aplicablesPorPeriodo, especiales, especialesPorPeriodo]) && (
        <>
          <strong>Descuentos sobre cargo</strong>
          {aplicables}
          {aplicablesPorPeriodo}
          {especiales}
          {especialesPorPeriodo}
        </>
      )}
      {some([sobreRecargos, sobreRecargosPorPeriodo]) && (
        <>
          <strong>Descuentos sobre recargo</strong>
          {sobreRecargos}
          {sobreRecargosPorPeriodo}
        </>
      )}
      {some([sobreRecargosMulta, sobreRecargosMultaPorPeriodo, sobreGastosMulta, sobreGastosMultaPorPeriodo]) && (
        <>
          <strong>Descuentos sobre multas</strong>
          {sobreRecargosMulta}
          {sobreRecargosMultaPorPeriodo}
          {sobreGastosMulta}
          {sobreGastosMultaPorPeriodo}
        </>
      )}
      {some([sobreGastos, sobreGastosPorPeriodo]) && (
        <>
          <strong>Descuentos sobre gastos</strong>
          {sobreGastos}
          {sobreGastosPorPeriodo}
        </>
      )}
      {some([sobreActualizacion, sobreActualizacionPorPeriodo]) && (
        <>
          <strong>Descuentos sobre actualizaci?n</strong>
          {sobreActualizacion}
          {sobreActualizacionPorPeriodo}
        </>
      )}
    </ul>
  ) : null;

  return (
    <Tooltip title={title} onClick={(e) => e.stopPropagation()}>
      {currencyFormater(txt)}
    </Tooltip>
  );
};

const descripcionRender = (txt, record) => (record?.fecha_de_vencimiento ? (
  <>
    <Tooltip
      title={`Fecha de Vencimiento ${toMoment(record.fecha_de_vencimiento).format()}`}
    >
      {txt}
    </Tooltip>
    {' '}
    {tooltipoInfo(record)}
  </>
) : (
  <>
    {' '}
    {txt}
    {' '}
    {tooltipoInfo(record)}
  </>
));

export const errorMessage = 'No fue posible obtener el adeudo del padrón';

const filterCopies = (padrones, padron) => padrones.filter((p) => p.isCopy
  && p.id === padron.id && p.tipo_de_padron === padron.tipo_de_padron);

export const filterCargosTramites = (cargos) => (c) => {
  if (c?.cargo_padre) {
    const cargoPadre = cargos.find((e) => e.id === c?.cargo_padre);
    return cargoPadre?.tramite;
  }
  return c?.tramite;
};

export const TableAdeudoPadron = ({
  cargos = [],
  callback = () => { },
  selectedRowKeys = [],
  copies = [],
  moveToCopy,
  tableProps = {},
  tipoDePadron,
  disabled = false,
}) => {
  const _onRowChildren = (record, keys) => {
    let cloneKeys = [...keys];
    const cargoChild = cargos.find((e) => e.cargo_padre === record.id && !e.es_accesorio);
    const cargosAccesorios = cargos.filter((e) => e.cargo_padre === record.id && e.es_accesorio);
    cloneKeys = cloneKeys.filter((k) => !cargosAccesorios.map((e) => e.id).includes(k));
    cloneKeys = cloneKeys.filter((a) => a !== record.id);
    cloneKeys = [...new Set(cloneKeys)];
    if (cargoChild) {
      return _onRowChildren(cargoChild, cloneKeys);
    }
    return cloneKeys;
  };

  const _onRowFather = (record, keys) => {
    let cloneKeys = [...keys];
    const cargoPadre = cargos.find((e) => (e.id === record.cargo_padre));
    const cargosAccesorios = cargos.filter((e) => e.cargo_padre === record.id && e.es_accesorio);
    cloneKeys.push(record.id);
    cloneKeys = cloneKeys.concat(cargosAccesorios.map((e) => e.id));
    cloneKeys = [...new Set(cloneKeys)];
    if (cargoPadre) {
      return _onRowFather(cargoPadre, cloneKeys);
    }
    return cloneKeys;
  };

  const onRow = (record) => {
    if ([padronesEnum.INFRACCIONES, padronesEnum.MULTAS].includes(tipoDePadron)) {
      callback(selectedRowKeys.length ? [] : cargos.map((c) => c.id));
    } else {
      const checked = !selectedRowKeys.some((k) => k === record.id);
      const keys = checked
        ? _onRowFather(record, selectedRowKeys) : _onRowChildren(record, selectedRowKeys);
      callback(keys);
    }
  };

  const Overlay = ({ cargo }) => {
    const items = copies
      .filter((p) => !p.sistema?.id || p.sistema?.id === cargo.sistema?.id)
      .map((p) => ({
        label: `${padronToString(p, p.tipo_de_padron)} (Copia ${p.copyNumber})`,
        key: p.key,
      }))
      .concat({
        label: 'Copia Nueva',
        key: 'new',
      });
    return (
      <Menu
        items={items}
        onClick={({ key: _key, domEvent }) => {
          domEvent.stopPropagation();
          moveToCopy(_key, cargo);
        }}
      />
    );
  };

  Overlay.propTypes = {
    cargo: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
  };

  const columns = [
    isDev && {
      titleText: (
        <span>
          ID/TC (DEV)
        </span>
      ),
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    isDev && {
      titleText: (
        <span>
          Tipo de Cargo
          <b>(DEV)</b>
        </span>
      ),
      dataIndex: 'tipo_de_cargo',
      key: 'tipo_de_cargo',
      width: 200,
      render: (val) => val.id || '',
    },
    {
      titleText: 'Periodo Fiscal',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 150,
      render: (_, record) => record.tipo_de_cargo?.periodo_fiscal?.periodo,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 350,
      render: descripcionRender,
      // fixed: isDev ? 'left' : undefined,
    },
    {
      titleText: 'Adeudo Total',
      dataIndex: 'adeudo_total',
      key: 'adeudo_total',
      render: (val) => currencyFormater(val),
      align: 'right',
      width: 150,
    },
    {
      titleText: 'Importe Base',
      dataIndex: 'importe',
      key: 'importe',
      width: 150,
      align: 'right',
      render: (val) => currencyFormater(val),
    },
    {
      titleText: 'Actualización',
      dataIndex: 'actualizaciones_base',
      key: 'actualizaciones_base',
      width: 125,
      align: 'right',
      render: (txt, record) => {
        const _actualizaciones = record.actualizaciones
          .filter((r) => r.estados_globales !== 5 && !r.saldado);
        const actualizaciones = _actualizaciones.length
          ? _actualizaciones.filter((i) => !i.es_multa).map((r) => (
            <li key={r.id}>
              {`${r.descripcion}: ${currencyFormater(r.importe_total)}`}
            </li>
          )) : null;

        return (
          <Tooltip title={actualizaciones?.length ? (<ul>{actualizaciones}</ul>) : null}>
            <span>{currencyFormater(txt)}</span>
          </Tooltip>
        );
      },
    },
    {
      titleText: 'Recargos',
      dataIndex: 'recargos_base',
      key: 'recargos_base',
      width: 150,
      align: 'right',
      render: (txt, record) => {
        const _recargos = record.recargos.filter((r) => r.estados_globales !== 5 && !r.saldado);
        const recargos = _recargos.length
          ? _recargos.filter((i) => !i.es_multa).map((r) => (
            <li key={r.id}>
              {`${r.descripcion}: ${currencyFormater(r.importe_total)}`}
            </li>
          )) : null;

        return (
          <Tooltip title={recargos?.length ? (<ul>{recargos}</ul>) : null}>
            <span>{currencyFormater(txt)}</span>
          </Tooltip>
        );
      },
    },
    {
      titleText: 'Multa',
      dataIndex: 'multas_base',
      key: 'multas_base',
      width: 125,
      align: 'right',
      // render: multasRender,
      render: (txt, record) => {
        const _recargos = record.recargos.filter((r) => r.estados_globales !== 5 && !r.saldado);
        const recargosMultas = _recargos.filter((i) => i.es_multa);
        const _gastos = record.gastos.filter((g) => g.estados_globales !== 5 && !g.saldado);
        const gastosMultas = _gastos.filter((i) => i.es_multa);
        const recargos = recargosMultas.map((r) => (
          <li key={r.id}>
            {`${r.descripcion}: ${currencyFormater(r.importe_total)}`}
          </li>
        ));
        const gastos = gastosMultas.map((g) => (
          <li key={g.id}>
            {`${g.descripcion}: ${currencyFormater(g.importe)}`}
          </li>
        ));
        const allMultas = [...recargos, ...gastos];
        return (
          <Tooltip title={allMultas?.length ? (<ul>{allMultas}</ul>) : null}>
            <span>{currencyFormater(txt)}</span>
          </Tooltip>
        );
      },
    },
    {
      titleText: 'Gastos',
      dataIndex: 'gastos_totales',
      key: 'gastos_totales',
      width: 125,
      align: 'right',
      // render: (txt, record) => renderAccesorios(txt, record, 'gastos'),
      render: (txt, record) => {
        const _gastos = record.gastos.filter((g) => g.estados_globales !== 5 && !g.saldado);
        const gastos = _gastos.filter((i) => !i.es_multa).map((g) => (
          <li key={g.id}>
            {`${g.descripcion}: ${currencyFormater(g.importe)}`}
          </li>
        ));
        return (
          <Tooltip title={gastos?.length ? (<ul>{gastos}</ul>) : null}>
            <span>{currencyFormater(txt)}</span>
          </Tooltip>
        );
      },
    },
    {
      titleText: 'Desc.',
      dataIndex: '_descuentos_totales_junto_accesorios',
      key: '_descuentos_totales_junto_accesorios',
      width: 125,
      align: 'right',
      render: descuentosRender,
    },
    !!moveToCopy && {
      titleText: 'Acciones',
      dataIndex: 'actions',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const enabled = !record.cargo_padre && cargos.length > 1
          && !cargos.some((e) => e.cargo_padre === record.id && !e.es_accesorio)
          && !record.disabledByAlert;
        if (record.es_accesorio) return null;
        return (
          <Dropdown
            overlay={<Overlay cargo={record} />}
            placement="top"
            arrow={{ pointAtCenter: true }}
            disabled={!enabled}
          >
            <Button
              onClick={(e) => e.stopPropagation()}
              type="link"
              disabled={!enabled}
            >
              Mover a copia
            </Button>
          </Dropdown>
        );
      },
    },
  ].filter((e) => e);

  const isDisabled = (record) => disabled || (record.es_accesorio && record.es_requerible)
    || (record?.convenios && [1, 4].includes(record?.convenios?.estados_globales))
    || record.tipo_de_cargo.es_pago_obligatorio || !!record.tramite
    || record.disabled || record.disabledByAlert;

  return (
    <Table
      data={cargos}
      mobileColIndex={2}
      rowSelection={{
        selectedRowKeys,
        onChange: (keys) => callback(keys),
        hideSelectAll: disabled || cargos.some((c) => c.disabledByAlert),
        renderCell: (checked, record) => (
          <Checkbox
            checked={checked}
            disabled={isDisabled(record)}
          />
        ),
      }}
      allowSort={false}
      noControls
      onRow={(record) => ({
        onClick: (e) => {
          e.stopPropagation();
          if (!isDisabled(record)) {
            onRow(record);
          }
        },
      })}
      cols={columns}
      pageSize={Infinity}
      hasAllViewPermission
      rowClassName={({ adeudo_total, es_accesorio }) => {
        let className = '';
        if (Number.isNaN(adeudo_total)) {
          className += 'warn-row ';
        }
        if (!es_accesorio) {
          className += 'bold';
        }
        return className;
      }}
      rowKey="id"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...tableProps}
    />
  );
};

export const getTotalPadron = (cargos, selectedRowKeys) => {
  const selected = cargos.filter((cargo) => selectedRowKeys.includes(cargo.id));
  const reduced = selected.reduce((accum, curr) => accum + curr.adeudo_total, 0);
  return round(roundToTwo((reduced * 100) / 100)) || 0;
};
export const getTotalSinRound = (cargos, selectedRowKeys) => {
  const selected = cargos.filter((cargo) => selectedRowKeys.includes(cargo.id));
  const reduced = selected.reduce((accum, curr) => accum + curr.adeudo_total, 0);
  return roundToTwo((reduced * 100) / 100) || 0;
};

const PanelPadron = ({
  padron,
  mostrarCobro,
  canAddCargos,
  pinPadron,
  setPinPadron,
  cargos,
  selectedRowKeys,
}) => {
  const dispatch = useDispatch();
  const timeoutRef = useRef();
  const theme = useSelector(({ app }) => app.theme);
  const ciudadanoPublicoGral = useSelector(({ auth }) => auth.entidad.ciudadano_publico_en_general);
  const tiposDePadrones = useSelector(({ catalogs }) => catalogs.padrones);
  const cargosPublicoGeneral = useSelector(({ caja }) => caja.cargosPublicoGeneral);
  const padrones = useSelector(({ caja }) => caja.padrones);
  const [loading, setLoading] = useState(false);
  const [_message, setMessage] = useState();
  const [loadingDocumentoConvenio, setLoadingDocumentoConvenio] = useState(false);
  const esPublicoGeneral = (padron.id === ciudadanoPublicoGral && padron.tipo_de_padron === 1);
  const copies = useMemo(() => filterCopies(padrones, padron), [padrones]);

  const total = useMemo(() => getTotalPadron(cargos, selectedRowKeys), [selectedRowKeys, cargos]);

  const getCargosPublicoGeneral = async () => {
    setLoading(true);
    const _cargos = await postConsultarCargosPublicoGeneral(cargosPublicoGeneral);
    if (_cargos) {
      dispatch({
        type: SET_CARGOS_PADRON,
        payload: {
          key: padron.key,
          cargos: _cargos,
        },
      });
    } else {
      setMessage(errorMessage);
    }
    setLoading(false);
  };

  const getCargosPadron = async (_timeout = false) => {
    setLoading(true);
    const response = await postConsultarCargosPorPadron(padron.tipo_de_padron, padron.id, _timeout);
    if (response.detail || response.status) {
      setMessage(titleize(response.detail || response.status));
      timeoutRef.current = setTimeout(() => getCargosPadron(_timeout), 5000);
    } else if (response.cargos) {
      dispatch({
        type: SET_CARGOS_PADRON,
        payload: {
          key: padron.key,
          cargos: response.cargos,
          plantillas: response.plantillas,
          tramites_bloqueando_adeudos: response.tramites_bloqueando_adeudos,
          alertas: response.alerta?.filter((a) => a?.estados_globales !== 5),
          convenios: response.convenios,
        },
      });
      if (response?.alertas?.filter((a) => a?.estados_globales !== 5)?.length) {
        dispatch({ type: SET_PIN_PADRON_ALERTAS, payload: response });
      }
      setLoading(false);
    } else {
      setMessage(errorMessage);
      setLoading(false);
    }
  };

  const fetchCargos = async (_timeout = false) => {
    setLoading(true);
    if (_message === errorMessage) {
      setMessage();
    }
    if (esPublicoGeneral) {
      await getCargosPublicoGeneral();
    } else {
      await getCargosPadron(_timeout);
    }
  };

  const clearRef = () => timeoutRef.current && clearTimeout(timeoutRef.current);

  useEffect(() => {
    const { fechaUltimaActualizacionCargos, requiereFetch } = padron;
    const now = toMoment(new Date());
    const requiereUpdate = !!fechaUltimaActualizacionCargos
      && now.format() !== toMoment(fechaUltimaActualizacionCargos).format();
    if (requiereFetch || (requiereUpdate && !padron.isCopy && !esPublicoGeneral)) {
      fetchCargos();
    }
    return clearRef;
  }, [padron]);

  const onClickDelete = () => {
    if (pinPadron?.key === padron.key) {
      setPinPadron(padrones[0]);
    }
    dispatch({
      type: REMOVE_PADRON,
      payload: padron,
    });
  };

  const canAdd = !padron.hasAlertasRestrictivas
    && !padron.isCopy
    && !padron.tramite
    && !padron.sistema
    && !padron.folio_de_referencia
    && !padron.convenio;

  const onClickAdd = (e) => {
    e.stopPropagation();
    if (canAddCargos) {
      dispatch({
        type: SET_PIN_PADRON_AGREGAR_CARGOS,
        payload: padron,
      });
    } else {
      message.warn('No cuenta con los permisos para agregar cargos');
    }
  };

  const onClickDocumentoConvenio = async (e) => {
    e.stopPropagation();
    setLoadingDocumentoConvenio(true);
    const _convenio = await getConvenio(padron.convenio);
    const base64 = await postObtenerReporte({
      ID: REPORTE_DOCUMENTO_CONVENIO,
      Parametros: [
        {
          Nombre: 'convenio',
          TipoParametro: 'Entero',
          Valor: padron.convenio,
        },
        {
          Nombre: 'personal_autorizado',
          TipoParametro: 'Entero',
          Valor: _convenio?.personal_de_convenio,
        },
      ],
    });
    if (base64) {
      downloadBase64(base64, `Convenio ${padron.convenioFolio || padron.convenio}.pdf`);
    } else {
      message.warn('No se pudo obtener el documento del convenio');
    }
    setLoadingDocumentoConvenio(false);
  };

  const LoadingWrapper = ({ children }) => {
    if (_message === errorMessage && !loading) {
      return (
        <Popconfirm
          placement="top"
          title="Puede reintentar obtener el adeudo o eliminar el padrón de la lista"
          okText="Reintentar"
          cancelText="Eliminar"
          cancelButtonProps={{ danger: true }}
          onCancel={onClickDelete}
          onConfirm={() => fetchCargos(true)}
        >
          <Spin
            spinning
            tip={(<span className="warn bold">{errorMessage}</span>)}
            indicator={<ExclamationCircleOutlined style={{ fontSize: 24 }} className="warn" />}
          >
            {children}
          </Spin>
        </Popconfirm>
      );
    }

    if (!padron.fechaUltimaActualizacionCargos && !padron.requiereFetch && !loading) {
      return (
        <Popconfirm
          placement="top"
          title="¿Seguro que desea consultar el adeudo del padrón?"
          okText="Consultar"
          cancelButtonProps={{ style: { display: 'none' } }}
          onConfirm={() => fetchCargos(true)}
        >
          <Spin
            spinning
            tip="Presione para consultar adeudo del padrón"
            indicator={<ExclamationCircleOutlined style={{ fontSize: 24 }} className="warn" />}
          >
            {children}
          </Spin>
        </Popconfirm>
      );
    }

    return (
      <Spin spinning={loading} tip={_message || 'Cargando...'}>
        {children}
      </Spin>
    );
  };

  const moveToCopy = (to, cargo) => {
    const _accesorios = cargos
      .filter((c) => c.cargo_padre === cargo.id && c.es_accesorio);
    if (to !== 'new') {
      // Actualizando Cargos de padrón copia
      dispatch({
        type: SET_CARGOS_PADRON,
        payload: {
          key: to,
          cargos: [
            cargo,
            ..._accesorios,
          ],
        },
      });
    } else {
      // Agregando copia
      dispatch({
        type: ADD_PADRON,
        payload: {
          ...padron,
          key: `${padron.key}__${cargo.id}`,
          cargos: [
            cargo,
            ..._accesorios,
          ],
          isCopy: true,
          copyNumber: copies.length + 1,
        },
      });
    }
  };

  const deleteDisabled = (esPublicoGeneral && !padron.searchedByFolio) || padron.disabled
    // || (padron.tramite && !padron.searchedByFolio) || padron.sistema
    || (!padron.isCopy && padrones.some((e) => e.id === padron.id && e.isCopy));

  const getTitle = (_padron) => {
    let output = '';
    if (_padron.convenio && _padron.folio_de_referencia) {
      output = 'Orden de Pago de Convenio';
    } else if (_padron.tramite) {
      output = 'Trámite';
    } else if (_padron.sistema) {
      output = 'Sistema';
    } else if (_padron.folio_de_referencia) {
      output = 'Orden de Pago';
    }
    if (_padron.convenio) {
      output = output?.length ? `${output} - Convenio` : 'Convenio';
    }
    return output;
  };

  const hasBloqueoTramite = !!padron.tramites_bloqueando_adeudos?.length
    && !padron.tramites_bloqueando_adeudos.includes(padron.tramite?.id);

  const getKeys = (_padron) => {
    let output = '';

    if (_padron.folio_de_referencia) {
      output = _padron.folio_de_referencia;
    } else if (_padron?.tramite) {
      output = _padron.tramite.folio;
    } else if (_padron?.sistema) {
      output = _padron.sistema.descripcion;
    }

    if (_padron.convenio) {
      output = `${output} - ${_padron?.convenioFolio}`;
    }

    return output;
  };

  return (
    <React.Fragment key={`frag_${padron.key}`}>
      <Collapse>
        <Collapse.Panel
          key={padron.key}
          collapsible={(loading || _message === errorMessage
            || (!padron.fechaUltimaActualizacionCargos && !padron.requiereFetch)) && 'disabled'}
          header={(
            <LoadingWrapper>
              <Row justify="space-between" className="collapse-caja-row">
                <Col xs={24} sm={24} md={8} className="padron-label">
                  <Text>
                    {tiposDePadrones.find((e) => e.id === padron.tipo_de_padron)?.descripcion}
                    {': '}
                  </Text>
                  {padronToString(padron, padron.tipo_de_padron)}
                  {padron.copyNumber && <b>{` (Copia ${padron.copyNumber})`}</b>}
                  {(padron.tramite
                  || padron.sistema
                  || padron.folio_de_referencia
                  || padron?.convenio) && (
                    <>
                      {' - '}
                      &#40;
                      <b>
                        {getTitle(padron)}
                      </b>
                      {': '}
                      {getKeys(padron)}
                      &#41;
                    </>
                  )}
                </Col>
                <Col xs={24} sm={24} md={3} style={{ textAlign: 'end' }}>
                  <Text>
                    {currencyFormater(total)}
                  </Text>
                </Col>
                <Col xs={24} sm={24} md={3}>
                  <Text>
                    {`(${selectedRowKeys?.length || 0}/${cargos?.length || 0})`}
                  </Text>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={8}
                  className="action-buttons"
                >
                  <Row justify="end">
                    {!padron.convenio && !hasBloqueoTramite && !padron.hasBloqueoAlertas && (
                      <Col flex="20%">
                        <Button
                          type="link"
                          className="stronger"
                          icon={padron.key === pinPadron?.key
                            ? <PushpinFilled className="primary-text" /> : <PushpinOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPinPadron(padron);
                          }}
                          disabled={!!padron.disabled}
                        />
                      </Col>
                    )}
                    {canAdd && (
                      <Col flex="20%">
                        <Button
                          type="link"
                          className="stronger"
                          icon={<PlusOutlined />}
                          onClick={onClickAdd}
                        />
                      </Col>
                    )}
                    {(padron.hasAlertas) && (
                      <Col flex="20%">
                        <Tooltip title={padron.hasBloqueoAlertas ? 'Adeudo bloqueado por alerta(s) restictiva(s)' : 'Padrón tiene alertas'}>
                          <Button
                            type="link"
                            className={`stronger no-color ${padron.hasBloqueoAlertas ? 'warning' : 'info'}`}
                            icon={padron.hasBloqueoAlertas
                              ? <ExclamationCircleOutlined className="warning" />
                              : <InfoCircleOutlined className="info" />}
                            onClick={() => dispatch({
                              type: SET_PIN_PADRON_ALERTAS,
                              payload: padron,
                            })}
                          />
                        </Tooltip>
                      </Col>
                    )}
                    {(!esPublicoGeneral && !padron.isCopy && !padron.tramite && !padron.sistema && !padron.convenio) && (
                      <Col flex="20%">
                        <Button
                          type="link"
                          className={`stronger ${padron.requireData ? 'danger' : ''}`}
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch({
                              type: SET_PIN_PADRON_INFO,
                              payload: padron,
                            });
                          }}
                          disabled={esPublicoGeneral}
                        />
                      </Col>
                    )}
                    <Col flex="20%">
                      {!padron.disabled && !padron.convenio && (
                        <Tooltip title={`Observaciones del padrón: ${padron?.observaciones}`}>
                          <Button
                            type="link"
                            className="stronger"
                            icon={<CommentOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch({
                                type: SET_PIN_PADRON_COMENTARIO,
                                payload: padron,
                              });
                            }}
                          />
                        </Tooltip>
                      )}
                    </Col>
                    {(padron.convenio && mostrarCobro) && (
                      <Col flex="20%">
                        <Button
                          size="large"
                          style={{ borderRadius: 5 }}
                          icon={<PieChartOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch({
                              type: SET_PADRON_PAGO_CONVENIO,
                              payload: padron,
                            });
                          }}
                        >
                          Cobrar convenio
                        </Button>
                      </Col>
                    )}
                    {!!padron.convenio && (
                      <Col flex="20%">
                        <Tooltip title="Consultar documento del convenio">
                          <Button
                            type="link"
                            className="stronger"
                            icon={<FilePdfOutlined />}
                            loading={loadingDocumentoConvenio}
                            onClick={onClickDocumentoConvenio}
                          />
                        </Tooltip>
                      </Col>
                    )}
                    <Col flex="20%">
                      <Button
                        type="link"
                        className="danger stronger"
                        icon={<DeleteOutlined />}
                        disabled={deleteDisabled}
                        onClick={() => onClickDelete()}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </LoadingWrapper>
            )}
          style={{
            ...(pinPadron?.key === padron?.key ? {
              borderStyle: 'solid',
              borderColor: theme.cardTitleColor === '#FFFFFF' ? theme.primaryText
                : theme.cardTitleColor,
            } : null),
            cursor: 'pointer',
          }}
          forceRender
        >
          <TableAdeudoPadron
            cargos={cargos}
            selectedRowKeys={selectedRowKeys}
            tipoDePadron={padron.tipo_de_padron}
            callback={(keys) => {
              const _keys = keys.filter((k) => cargos.find((c) => {
                if (c?.convenios && c?.convenios?.estados_globales === 5) {
                  return true;
                }
                if (c?.convenios && c?.convenios?.estados_globales === 1) {
                  return false;
                }
                if (c.id === k) {
                  return true;
                }
                return false;
              }));
              dispatch({
                type: SET_SELECTEDROWKEYS,
                payload: {
                  key: padron.key,
                  selectedRowKeys: _keys,
                },
              });
            }}
            moveToCopy={mostrarCobro && !padron.isCopy && !esPublicoGeneral
              ? moveToCopy : null}
            copies={copies}
            tableProps={{ loading }}
            disabled={!!padron.disabled || hasBloqueoTramite}
          />
        </Collapse.Panel>
      </Collapse>
    </React.Fragment>
  );
};

export default PanelPadron;
